import {
  GenerateBarcodeInput,
  GenerateBarcodeResultTypenames,
  Mutation,
} from '@varo/graphql-schema-types';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import GenerateDepositBarcode from 'services/graphql/mutations/MoveMoney/GenerateDepositBarcode';
import { useDefaultErrorStrings } from 'shared/hooks/errorFormatters/useDefaultError/useDefaultErrorStrings';
import useCancellableMutation, {
  AbortError,
} from 'shared/hooks/useCancellableMutation/useCancellableMutation';
import { ErrorScreenType } from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import { logErrorWithContext } from 'shared/utils/sentry/sentry';
import {
  ErrorSeverity,
  FeatureArea,
  ResponsibleTeam,
} from 'shared/utils/sentry/sentry.constants';

const ACTIVE_BARCODE_EXISTS_CODE = 'ACTIVE_BARCODE_EXISTS' as const;

interface ErrorScreen {
  screenType: ErrorScreenType.Error;
  messageHeadline: string;
  messageBody: string;
  hideRetryButton?: boolean;
}

interface BarcodeData {
  barcodeId: string;
  gdBarcodeIdExpiration: string;
}

interface RefreshState {
  hasRefreshError: boolean;
  attempts: number;
}

interface UseGenerateDepositBarcodeResult {
  isGenerating: boolean;
  barcodeError: boolean;
  barcodeData: BarcodeData | null;
  errorScreen: ErrorScreen | null;
  refreshState: RefreshState;
  generateBarcode: (
    input: GenerateBarcodeInput,
    isRefresh?: boolean,
  ) => Promise<void>;
  resetError: () => void;
  resetRefreshError: () => void;
  abortGenerateBarcode: () => void;
}

export const useGenerateDepositBarcode =
  (): UseGenerateDepositBarcodeResult => {
    const [, executeMutation, abortMutation] = useCancellableMutation<Mutation>(
      GenerateDepositBarcode,
    );

    const { t: translatedText } = useTranslation('moveMoney', {
      keyPrefix: 'depositCash.barcode',
    });
    const { defaultTitle, defaultMessage } = useDefaultErrorStrings();
    const [errorScreen, setErrorScreen] = useState<ErrorScreen | null>(null);
    const [barcodeError, setBarcodeError] = useState(false);
    const [barcodeData, setBarcodeData] = useState<BarcodeData | null>(null);
    const [isGenerating, setIsGenerating] = useState(false);
    const [refreshError, setRefreshError] = useState(false);
    const [refreshErrorCount, setRefreshErrorCount] = useState(0);

    const logError = (
      error: unknown,
      severity: ErrorSeverity,
      input: GenerateBarcodeInput,
      isRefresh: boolean,
      additionalContext: Record<string, unknown> = {},
    ) => {
      logErrorWithContext(error, {
        featureArea: FeatureArea.PAYMENTS,
        responsibleTeam: ResponsibleTeam.PAYMENTS_TRANSFER,
        severity,
        additionalContext: {
          input,
          isRefresh,
          ...additionalContext,
        },
      });
    };

    const generateBarcode = useCallback(
      async (
        input: GenerateBarcodeInput,
        isRefresh: boolean = false,
      ): Promise<void> => {
        const handleError = (
          isRefresh: boolean,
          errorTitle: string = defaultTitle,
          errorMessage: string = defaultMessage,
          options?: { hideRetryButton?: boolean },
        ) => {
          if (isRefresh && !options?.hideRetryButton) {
            setRefreshError(true);
            setRefreshErrorCount((prev) => prev + 1);
            return;
          }

          if (options?.hideRetryButton) {
            setBarcodeError(true);
            setErrorScreen({
              screenType: ErrorScreenType.Error,
              messageHeadline: errorTitle,
              messageBody: errorMessage,
              hideRetryButton: true,
            });
            setRefreshError(false);
            return;
          }

          setBarcodeError(true);
          setErrorScreen({
            screenType: ErrorScreenType.Error,
            messageHeadline: errorTitle,
            messageBody: errorMessage,
          });
        };

        setErrorScreen(null);
        setBarcodeError(false);
        if (!isRefresh) {
          setBarcodeData(null);
        }
        setRefreshError(false);
        setIsGenerating(true);

        try {
          const mutationResult = await executeMutation(
            { input },
            { requestPolicy: 'network-only' },
          );
          const payload = mutationResult.data?.generateDepositBarcode;

          if (!payload || mutationResult.error) {
            handleError(isRefresh);
            if (mutationResult.error) {
              logError(
                mutationResult.error,
                ErrorSeverity.HIGH,
                input,
                isRefresh,
                {
                  errorMessage: mutationResult.error.message,
                },
              );
            }
            setIsGenerating(false);
            return;
          }

          if (
            payload.__typename ===
            GenerateBarcodeResultTypenames.GenerateBarcodeError
          ) {
            const isActive = payload.code === ACTIVE_BARCODE_EXISTS_CODE;

            let computedTitle: string;
            let computedMessage: string;

            if (isActive) {
              computedTitle = payload.title;
              computedMessage = payload.message;
            } else {
              computedTitle = translatedText('barcodeError.title');
              computedMessage = translatedText('barcodeError.subtitle');
            }

            handleError(
              isRefresh,
              computedTitle,
              computedMessage,
              isActive ? { hideRetryButton: true } : undefined,
            );
            if (!isActive) {
              logError(
                new Error(payload.message ?? ''),
                ErrorSeverity.MEDIUM,
                input,
                isRefresh,
                {
                  code: payload.code,
                  title: payload.title,
                  message: payload.message,
                },
              );
            }

            setIsGenerating(false);
            return;
          }

          if (
            payload.__typename ===
            GenerateBarcodeResultTypenames.GenerateBarcodeSuccess
          ) {
            setBarcodeData({
              barcodeId: payload.gdBarcodeId,
              gdBarcodeIdExpiration: payload.gdBarcodeIdExpiration,
            });
            setIsGenerating(false);
            return;
          }

          handleError(isRefresh);
          setIsGenerating(false);
        } catch (err) {
          if (err instanceof AbortError) {
            setIsGenerating(false);
            return;
          }

          setIsGenerating(false);
          handleError(isRefresh);
          logError(err, ErrorSeverity.HIGH, input, isRefresh, {
            event: 'generateBarcodeException',
          });
        }
      },
      [executeMutation, defaultTitle, defaultMessage, translatedText],
    );

    const resetError = useCallback(() => {
      setBarcodeError(false);
      setErrorScreen(null);
      setBarcodeData(null);
      setRefreshError(false);
    }, []);

    const resetRefreshError = useCallback(() => {
      setRefreshError(false);
    }, []);

    return {
      isGenerating,
      barcodeError,
      barcodeData,
      errorScreen,
      refreshState: {
        hasRefreshError: refreshError,
        attempts: refreshErrorCount,
      },
      generateBarcode,
      resetError,
      resetRefreshError,
      abortGenerateBarcode: abortMutation,
    };
  };
